import { Directive, DirectiveBinding } from 'vue'
import './Ripple.less'
import { isObject } from '@/utils/is'
import { css, style, boundingRect } from '@/utils/dom'
import { stop, position } from '@/utils/event'

interface RippleCtx {
  enable: boolean
  modifiers: Recordable
}

type RippleTarget = HTMLElement & {
  __rippleCtx: RippleCtx
  __timer: ReturnType<typeof setTimeout>
}

function start(e: MouseEvent) {
  const el = e.currentTarget as RippleTarget
  const ctx = el.__rippleCtx

  if (ctx.modifiers.stop === true) {
    stop(e)
  }

  if (ctx.enable === true) {
    showRipple(e, el, ctx)
  }
}

function showRipple(e: MouseEvent, el: RippleTarget, ctx: RippleCtx) {
  const elPosition = style(el, 'position')
  if (elPosition === 'static') {
    css(el, { position: 'relative' })
  }

  const rippleWrapNode = document.createElement('span')
  const rippleNode = document.createElement('span')
  const { left, top, width, height } = boundingRect(el)
  const pos = position(e)
  const diameter = Math.sqrt(width * width + height * height)
  const radius = diameter / 2
  const centerX = (width - diameter) / 2 + 'px'
  const centerY = (height - diameter) / 2 + 'px'

  const leftX = (ctx.modifiers.center ? centerX : pos.x - left - radius) + 'px'
  const topY = (ctx.modifiers.center ? centerY : pos.y - top - radius) + 'px'

  rippleWrapNode.classList.add('ripple-wrap')
  rippleNode.classList.add('ripple')
  css(rippleNode, {
    width: diameter + 'px',
    height: diameter + 'px',
    opacity: 0,
    transform: `translate3d(${leftX}, ${topY}, 0) scale3d(.2, .2, 1)`,
  })

  rippleWrapNode.appendChild(rippleNode)
  el.appendChild(rippleWrapNode)

  el.__timer = setTimeout(() => {
    rippleNode.classList.add('ripple--enter')
    rippleNode.style.transform = `translate3d(${centerX},${centerY},0) scale3d(1,1,1)`
    rippleNode.style.opacity = '0.2'

    el.__timer = setTimeout(() => {
      rippleNode.classList.remove('ripple--enter')
      rippleNode.classList.add('ripple--leave')
      rippleNode.style.opacity = '0'

      el.__timer = setTimeout(() => {
        rippleWrapNode.remove()
        el.style.position = elPosition
      }, 275)
    }, 250)
  }, 50)
}

function updateModifers(ctx: RippleCtx, binding: DirectiveBinding) {
  const { modifiers } = binding
  ctx.modifiers = {
    stop: modifiers.stop,
    center: modifiers.center,
  }
}

const ripple: Directive = {
  beforeMount(el: RippleTarget, binding) {
    const ctx: RippleCtx = {
      enable: binding.value !== false,
      modifiers: {},
    }
    updateModifers(ctx, binding)
    el.__rippleCtx = ctx
    el.addEventListener('click', start, false)
  },
  updated(el: RippleTarget, binding) {
    if (binding.value !== binding.oldValue) {
      const ctx = el.__rippleCtx
      ctx.enable = binding.value !== false
      if (ctx.enable && isObject(binding.value)) {
        updateModifers(ctx, binding)
      }
    }
  },
  unmounted(el: RippleTarget) {
    clearTimeout(el.__timer)
    el.removeEventListener('click', start)
    Reflect.deleteProperty(el, '__rippleCtx')
  },
}

export default {
  name: 'ripple',
  definition: ripple,
}
